import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { defaultErrorCallback } from '@chilipiper/service/src/api'
import { Loader, Box } from '@chilipiper/design-system'
import { parseQuery, getDomain } from '@chilipiper/utils'
import { injectCssPublic } from '@chilipiper/meetings-components/src/helpers/injectCss'
import { bookMe } from '@chilipiper/service'
import { WorkspaceProvider } from '@chilipiper/workspace'
import { InboundRouterByLink } from '@chilipiper/api-type-def'
import { defaultFetchLocale, LocaleProvider } from '@chilipiper/locale'
import { useFireRedirect } from 'app/guest-app/hooks/useFireRedirect'
import { useEnabledLanguages } from '../locale'
import { RouterLink } from './types'
import { useGuestRoute } from './hooks'
import { RouterGuestView } from './RouterGuestView'

interface GuestState {
  create: boolean
  guests: string[]
  settings?: InboundRouterByLink
  settingsLoading: boolean
}

const RouterGuestApp = () => {
  const { id, email, locale, campaignId, status } = parseQuery()
  const { routerName } = useParams<{ routerName: string }>()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [state, setState] = useState<GuestState>({
    create: !id && !email,
    settingsLoading: true,
    guests: [],
  })

  const { organizationName, routeSettings, isLoadingRouteSettings } = useGuestRoute(
    routerName,
    getDomain()
  )

  useFireRedirect(isLoadingRouteSettings, false, undefined, undefined, routeSettings?.form)

  const updatePageTitle = useCallback(() => {
    if (organizationName) {
      document.title = `Fill in your information | ${organizationName}`
    } else {
      document.title = 'Book a time'
    }
  }, [organizationName])

  const handleProspectRoute = (_: unknown, prospect: Record<string, string>) => {
    setIsLoading(true)
    if (prospect) {
      handleRoute(prospect?.id)
    } else {
      setError('Oops. It looks like some problem with SF access. Pleasy try again.')
    }
  }

  const handleRoute = useCallback(
    (id?: string) => {
      let search = id ? `?id=${id.replace('+', '%2B')}` : window.location.search.replace('+', '%2B')
      if (routerName) {
        const sign = search ? '&' : '?'
        search = `${search}${sign}router=${routerName}`
      }
      bookMe
        .router(getDomain(), search)
        .then(result => {
          if (!result.isProspectInSalesforce) {
            setState(prevState => ({
              ...prevState,
              create: true,
            }))
          } else if (result.link) {
            window.location.href = locale ? `${result.link}&locale=${locale}` : result.link
          } else {
            setError('Oops. We could not route you to assignee. Please try again')
          }
        })
        .catch(defaultErrorCallback)
    },
    [locale, routerName]
  )

  const handleRouterFlow = (result: RouterLink) => {
    const url = result.realtimeUrl || result.url
    if (url) {
      const params = new URLSearchParams({
        conciergeFormFilled: 'true',
      })
      if (campaignId) {
        params.append('campaignId', campaignId.toString())
      }
      if (status) {
        params.append('status', status.toString())
      }
      window.location.href = `${url}&${params}`
    } else if (result.redirect?.disqualifiedLink) {
      let link = result.redirect.disqualifiedLink
      if (!link.startsWith('http')) {
        link = `http://${link}`
      }
      window.location.href = link
    } else {
      window.location.reload()
    }
  }

  const handleGuestListUpdate = (guests: string[]) =>
    setState(prevState => ({ ...prevState, guests }))

  useEffect(() => {
    updatePageTitle()

    if (!isLoadingRouteSettings && routeSettings) {
      injectCssPublic(getDomain(), routerName, () => {
        setState(prevState => ({
          ...prevState,
          settingsLoading: false,
          settings: routeSettings,
        }))

        if (id || email) {
          handleRoute()
        }
      })
    }
  }, [handleRoute, id, isLoadingRouteSettings, routeSettings, routerName, updatePageTitle, email])

  const { enabledLanguages, isLoading: areEnabledLanguagesLoading } = useEnabledLanguages({
    workspaceId: state?.settings?.workspaceId,
  })

  if (areEnabledLanguagesLoading) {
    return <Loader />
  }

  return (
    <LocaleProvider fetchLocale={defaultFetchLocale} enabledLanguages={enabledLanguages}>
      <WorkspaceProvider>
        <Box h='100%' className='layout-guest'>
          <RouterGuestView
            error={error}
            onRouterFlow={handleRouterFlow}
            onProspectRoute={handleProspectRoute}
            routerName={routerName}
            guests={state.guests}
            create={state.create}
            onUpdateGuests={handleGuestListUpdate}
            isSettingsActive={state?.settings?.active}
            isLoading={isLoading}
          />
        </Box>
      </WorkspaceProvider>
    </LocaleProvider>
  )
}

export default RouterGuestApp
